import React, { useState, useEffect } from 'react';
import { Formik, Form } from 'formik';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { FaInstagram, FaFacebook, FaTwitter, FaLinkedin, FaYoutube, FaMedium, FaLink } from 'react-icons/fa';
import { FiPlus } from 'react-icons/fi';
import Breadcrumbs from '../../components/BreadCrumbs';
import StyledHeadingText from '../../components/Fonts/StyledHeadingText';
import InputField from '../../components/inputFields/InputField';
import FormikDropDown from '../../components/inputFields/FormikDropDown';
import MultiSelectDropDown from '../../components/inputFields/MultiSelectDropDown';
import ImagePickerField from '../../components/inputFields/ImagePickerField';
import HTMLEditor from '../../components/inputFields/HTMLEditor';
import SimpleHTMLEditor from '../../components/inputFields/SimpleHTMLEditor';
import HTMLPreview from '../../components/inputFields/HTMLPreview';
import Button from '../../components/buttons/Button';
import CustomLoading from '../../components/customLoading/CustomLoading';
import ShowModal from '../../utils/ShowModal';
import BlogCategoryFormPage from './BlogCategoryFormPage';
import BlogTagFormPage from './BlogTagFormPage';
import { createBlogPost, updateBlogPost, getBlogPostBySlug, updateSocialMediaForBlogPost, createSocialMediaForBlogPost } from '../../services/blogPost';
import { getBlogCategories } from '../../services/blogCategory';
import { getBlogTags } from '../../services/blogTag';
import TextareaField from '../../components/inputFields/TextField';

const BlogPostFormPage = () => {
    const { id } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const isEdit = !!id;

    const [rowData, setRowData] = useState(null);
    const [socialMediaId, setSocialMediaId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(false);
    const [imageFile, setImageFile] = useState(null);
    const [previewImage, setPreviewImage] = useState(null);
    const [categories, setCategories] = useState([]);
    const [tags, setTags] = useState([]);
    const [useSimpleEditor, setUseSimpleEditor] = useState(false);
    const [editorHeight, setEditorHeight] = useState(600);
    const [showCategoryModal, setShowCategoryModal] = useState(false);
    const [showTagModal, setShowTagModal] = useState(false);
    const [initialValues, setInitialValues] = useState({
        title: '',
        excerpt: '',
        content: '',
        category: null,
        tags: [],
        meta_title: '',
        meta_description: '',
        meta_keywords: '',
        status: 'draft',
        estimated_reading_time: 3,
        order: 0,
        is_featured: false,
        is_priority: false,
        platforum: 'rashailagro',
        // Social Media URLs
        instagram: '',
        facebook: '',
        twitter: '',
        linkedin: '',
        youtube: '',
        medium: '',
        other: ''
    });

    const validationSchema = Yup.object({
        title: Yup.string()
            .required('Title is required')
            .min(5, 'Title must be at least 5 characters')
            .max(255, 'Title must be less than 255 characters'),
        excerpt: Yup.string()
            .required('Excerpt is required')
            .max(500, 'Excerpt must be less than 500 characters'),
        content: Yup.string()
            .required('Content is required')
            .min(50, 'Content must be at least 50 characters'),
        category: Yup.string()
            .nullable(),
        tags: Yup.array()
            .of(Yup.string()),
        meta_title: Yup.string()
            .max(60, 'Meta title must be less than 60 characters'),
        meta_description: Yup.string()
            .max(160, 'Meta description must be less than 160 characters'),
        meta_keywords: Yup.string()
            .max(255, 'Meta keywords must be less than 255 characters'),
        status: Yup.string()
            .oneOf(['draft', 'published', 'archived'], 'Invalid status'),
        estimated_reading_time: Yup.number()
            .min(1, 'Reading time must be at least 1 minute')
            .max(120, 'Reading time must be less than 120 minutes'),
        order: Yup.number()
            .min(0, 'Order must be a positive number')
            .integer('Order must be a whole number'),
        is_featured: Yup.boolean(),
        is_priority: Yup.boolean(),
        platforum: Yup.string().required('Platform is required'),
        // Social Media URL validation
        instagram: Yup.string().url('Please enter a valid Instagram URL'),
        facebook: Yup.string().url('Please enter a valid Facebook URL'),
        twitter: Yup.string().url('Please enter a valid Twitter URL'),
        linkedin: Yup.string().url('Please enter a valid LinkedIn URL'),
        youtube: Yup.string().url('Please enter a valid YouTube URL'),
        medium: Yup.string().url('Please enter a valid Medium URL'),
        other: Yup.string().url('Please enter a valid URL')
    });

    useEffect(() => {
        fetchCategoriesAndTags();
        if (location.state) {
            setRowData(location.state.post);
            if (location.state.post.slug) {
                fetchPostData(location.state.post.slug);
            }
        }
    }, [location, id, isEdit]);


    const fetchCategoriesAndTags = async () => {
        try {
            const [categoriesRes, tagsRes] = await Promise.all([
                getBlogCategories('?is_active=true'),
                getBlogTags('?is_active=true')
            ]);

            if (categoriesRes.status === 200) {
                setCategories(categoriesRes.data.results.map(cat => ({
                    label: cat.name,
                    value: cat.id
                })));
            }

            if (tagsRes.status === 200) {
                setTags(tagsRes.data.results.map(tag => ({
                    label: tag.name,
                    value: tag.id
                })));
            }
        } catch (error) {
            console.error('Error fetching categories and tags:', error);
        }
    };

    const handleCategorySuccess = (newCategory) => {
        setCategories(prev => [...prev, { label: newCategory.name, value: newCategory.id }]);
        setInitialValues(prev => ({ ...prev, category: newCategory.id }));
        setShowCategoryModal(false);
    };

    const handleTagSuccess = (newTag) => {
        setTags(prev => [...prev, { label: newTag.name, value: newTag.id }]);
        // For multi-select, we add to the existing selected tags
        setInitialValues(prev => ({
            ...prev,
            tags: [...(prev.tags || []), newTag.id]
        }));
        setShowTagModal(false);
    };

    const fetchPostData = async (slug) => {
        try {
            setInitialLoading(true);
            const response = await getBlogPostBySlug(slug);
            if (response.status === 200) {
                const data = response.data;
                setPreviewImage(data.featured_image);
                const tagsArray = data.tags && Array.isArray(data.tags)
                    ? data.tags.map(tag => typeof tag === 'object' ? tag.id : tag).filter(Boolean)
                    : [];

                setInitialValues({
                    title: data.title || '',
                    excerpt: data.excerpt || '',
                    content: data.content || '',
                    category: data.category.id || null,
                    tags: tagsArray,
                    meta_title: data.meta_title || '',
                    meta_description: data.meta_description || '',
                    meta_keywords: data.meta_keywords || '',
                    status: data.status || 'draft',
                    estimated_reading_time: data.estimated_reading_time || 3,
                    order: data.order || 0,
                    is_featured: data.is_featured || false,
                    is_priority: data.is_priority || false,
                    platforum: data.platforum || 'rashailagro',
                    // Social Media URLs
                    instagram: data?.social_media?.instagram || '',
                    facebook: data?.social_media?.facebook || '',
                    twitter: data?.social_media?.twitter || '',
                    linkedin: data?.social_media?.linkedin || '',
                    youtube: data?.social_media?.youtube || '',
                    medium: data?.social_media?.medium || '',
                    other: data?.social_media?.other || ''
                });
                setSocialMediaId(data?.social_media?.id);
            }
        } catch (error) {
            console.error('Error fetching post:', error);
            toast.error('Failed to fetch post data');
            navigate('/blog/posts');
        } finally {
            setInitialLoading(false);
        }
    };

    const handleImageSelect = (file) => {
        setImageFile(file);
        setPreviewImage(URL.createObjectURL(file));
    };

    const handleImageClear = () => {
        setImageFile(null);
        setPreviewImage(null);
    };

    const handleSubmit = async (values, { setSubmitting }) => {
        try {
            setLoading(true);

            const formData = new FormData();
            const socialMediaData = {
                instagram: values.instagram,
                facebook: values.facebook,
                twitter: values.twitter,
                linkedin: values.linkedin,
                youtube: values.youtube,
                medium: values.medium,
                other: values.other
            };
            formData.append('title', values.title);
            formData.append('excerpt', values.excerpt);
            formData.append('content', values.content);
            formData.append('meta_title', values.meta_title || '');
            formData.append('meta_description', values.meta_description || '');
            formData.append('meta_keywords', values.meta_keywords || '');
            formData.append('status', values.status);
            formData.append('estimated_reading_time', values.estimated_reading_time);
            formData.append('order', values.order);
            formData.append('is_featured', values.is_featured);
            formData.append('is_priority', values.is_priority);
            formData.append('platforum', values.platforum);

            if (values.category) {
                formData.append('category', values.category);
            }

            if (values.tags && values.tags.length > 0) {
                const tagsArray = Array.isArray(values.tags) ? values.tags : [];

                const validTags = tagsArray.filter(tag => {
                    if (typeof tag === 'string' && tag.trim()) {
                        const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
                        return uuidRegex.test(tag.trim());
                    }
                    return false;
                });

                validTags.forEach(tag => {
                    formData.append('tags', tag.trim());
                });
            }

            if (imageFile) {
                formData.append('featured_image', imageFile);
            }

            // Social media data is now a plain object, no need to append here

            let response;
            if (isEdit) {
                response = await updateBlogPost({
                    id: id,
                    data: formData
                });
            } else {
                response = await createBlogPost(formData);
            }

            if (response.status === 200 || response.status === 201) {
                socialMediaData.blog = response.data.id;

                try {
                    let socialMediaResponse;
                    if (socialMediaId) {
                        socialMediaResponse = await updateSocialMediaForBlogPost({
                            id: socialMediaId,
                            data: socialMediaData
                        });
                    } else {
                        socialMediaResponse = await createSocialMediaForBlogPost(socialMediaData);
                    }

                    if (socialMediaResponse.status === 200 || socialMediaResponse.status === 201) {
                        toast.success(`Social media links ${isEdit ? 'updated' : 'added'} successfully`);
                    } else {
                        toast.warning(`Social media links could not be ${isEdit ? 'updated' : 'added'}, but the post was saved`);
                    }
                } catch (smError) {
                    console.error('Error updating social media:', smError);
                    toast.warning(`Post saved, but failed to ${isEdit ? 'update' : 'create'} social media links`);
                }

                toast.success(`Post ${isEdit ? 'updated' : 'created'} successfully`);
                navigate('/blog/posts');
            } else {
                toast.error(`Failed to ${isEdit ? 'update' : 'create'} post`);
            }
        } catch (error) {
            console.error('Error submitting form:', error);
            toast.error(`Failed to ${isEdit ? 'update' : 'create'} post`);
        } finally {
            setLoading(false);
            setSubmitting(false);
        }
    };

    const handleCancel = () => {
        navigate('/blog/posts');
    };

    if (initialLoading) {
        return (
            <div className="flex justify-center items-center py-12">
                <CustomLoading size={100} />
            </div>
        );
    }

    return (
        <>
            <Breadcrumbs items={[
                { name: 'Home', link: '/dashboard' },
                { name: 'Blog', link: '/blog/dashboard' },
                { name: 'Posts', link: '/blog/posts' },
                { name: isEdit ? 'Edit Post' : 'Create Post' },
            ]} />

            <div className="py-3">
                <div className="mb-6">
                    <StyledHeadingText text={isEdit ? 'Edit Blog Post' : 'Create New Blog Post'} />
                    <p className="text-sm text-gray-600 mt-1">
                        {isEdit ? 'Update blog post information' : 'Add a new blog post'}
                    </p>
                </div>

                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <Formik
                        initialValues={initialValues}
                        validationSchema={validationSchema}
                        onSubmit={handleSubmit}
                        enableReinitialize={true}
                        key={isEdit ? `edit-${id}-${initialValues.category}-${initialValues.tags.join(',')}` : `create-${initialValues.category}-${initialValues.tags.join(',')}`}
                    >
                        {({ values, setFieldValue, isSubmitting }) => (
                            <Form className="space-y-6">
                                {/* Basic Information */}
                                <div className="space-y-4">
                                    <h3 className="text-lg font-medium text-gray-900">Basic Information</h3>

                                    <div className='grid grid-cols-1 md:grid-cols-3 gap-6'>
                                        <InputField
                                            name="title"
                                            label="Title"
                                            placeholder="Enter blog post title"
                                            required
                                        />
                                        <InputField
                                            name="excerpt"
                                            label="Excerpt"
                                            placeholder="Enter short description for previews"
                                            type="textarea"
                                            rows={3}
                                            required
                                        />
                                        <FormikDropDown
                                            name="platforum"
                                            label="Platform"
                                            options={[
                                                { label: 'Rashail Agro', value: 'rashailagro' },
                                                { label: 'Rashail Tech Lab', value: 'rashailtechlab' },
                                                { label: 'Rashail Infotech', value: 'rashailinfotech' }
                                            ]}
                                            placeholder="Select platform"
                                        />
                                    </div>


                                    <div>
                                        <div className="flex items-center justify-between mb-2">
                                            <div className="flex items-center gap-4">
                                                <label className="block text-sm font-medium text-gray-700">
                                                    Content
                                                    <span className="text-red-500 ml-1">*</span>
                                                </label>
                                                <button
                                                    type="button"
                                                    onClick={() => setUseSimpleEditor(!useSimpleEditor)}
                                                    className="text-xs px-2 py-1 bg-gray-100 hover:bg-gray-200 rounded border"
                                                >
                                                    {useSimpleEditor ? 'Use Rich Editor' : 'Use Simple Editor'}
                                                </button>
                                                <div className="flex items-center gap-2">
                                                    <label className="text-xs text-gray-600">Height:</label>
                                                    <select
                                                        value={editorHeight}
                                                        onChange={(e) => setEditorHeight(Number(e.target.value))}
                                                        className="text-xs px-2 py-1 border rounded"
                                                    >
                                                        <option value={400}>Small (400px)</option>
                                                        <option value={500}>Medium (500px)</option>
                                                        <option value={600}>Large (600px)</option>
                                                        <option value={700}>Extra Large (700px)</option>
                                                        <option value={800}>Huge (800px)</option>
                                                    </select>
                                                </div>
                                            </div>
                                            <HTMLPreview
                                                content={values.content}
                                                title="Blog Content Preview"
                                            />
                                        </div>
                                        {useSimpleEditor ? (
                                            <SimpleHTMLEditor
                                                key="simple-editor"
                                                name="content"
                                                placeholder="Enter full blog content..."
                                                height={editorHeight}
                                                required
                                            />
                                        ) : (
                                            <HTMLEditor
                                                key="rich-editor"
                                                name="content"
                                                placeholder="Enter full blog content..."
                                                height={editorHeight}
                                                required
                                            />
                                        )}
                                    </div>
                                </div>

                                {/* Category and Tags */}
                                <div className="space-y-4">
                                    <h3 className="text-lg font-medium text-gray-900">Category & Tags</h3>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div>
                                            <div className="flex items-end gap-2">
                                                <div className="flex-1">
                                                    <FormikDropDown
                                                        name="category"
                                                        label="Category"
                                                        options={categories}
                                                        placeholder="Select a category"
                                                    />
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() => setShowCategoryModal(true)}
                                                    className="mb-1 p-2 bg-primary/10 text-primary rounded-md hover:bg-primary/20 transition-colors"
                                                    title="Add New Category"
                                                >
                                                    <FiPlus size={20} />
                                                </button>
                                            </div>
                                        </div>
                                        <div>
                                            <div className="flex items-center justify-between mb-1">
                                                <label className="block text-sm font-medium text-gray-700">
                                                    Tags
                                                </label>
                                                <button
                                                    type="button"
                                                    onClick={() => setShowTagModal(true)}
                                                    className="flex items-center gap-1 text-xs text-primary hover:underline"
                                                >
                                                    <FiPlus size={14} /> Add New Tag
                                                </button>
                                            </div>
                                            <MultiSelectDropDown
                                                name="tags"
                                                options={tags}
                                                placeholder="Select tags"
                                                value={values.tags || []}
                                                onChange={(selectedValues) => {
                                                    setFieldValue('tags', selectedValues);
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* SEO Information */}
                                <div className="space-y-4">
                                    <h3 className="text-lg font-medium text-gray-900">SEO Information</h3>

                                    <div className='grid grid-cols-1 md:grid-cols-3 gap-6'>
                                        <TextareaField
                                            name="meta_title"
                                            label="Meta Title"
                                            placeholder="SEO title (max 60 characters)"
                                        />
                                        <TextareaField
                                            name="meta_description"
                                            label="Meta Description"
                                            placeholder="SEO description (max 160 characters)"
                                        />
                                        <TextareaField
                                            name="meta_keywords"
                                            label="Meta Keywords"
                                            placeholder="SEO keywords (comma separated)"
                                        />
                                    </div>
                                </div>

                                {/* Publishing Settings */}
                                <div className="space-y-4">
                                    <h3 className="text-lg font-medium text-gray-900">Publishing Settings</h3>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div>
                                            <FormikDropDown
                                                name="status"
                                                label="Status"
                                                options={[
                                                    { label: 'Draft', value: 'draft' },
                                                    { label: 'Published', value: 'published' },
                                                    { label: 'Archived', value: 'archived' }
                                                ]}
                                            />
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div>
                                            <InputField
                                                name="estimated_reading_time"
                                                label="Reading Time (minutes)"
                                                type="number"
                                                placeholder="3"
                                                min="1"
                                                max="120"
                                            />
                                        </div>
                                        <div>
                                            <InputField
                                                name="order"
                                                label="Order"
                                                type="number"
                                                placeholder="0"
                                                min="0"
                                            />
                                        </div>
                                    </div>

                                    <div className="flex space-x-6">
                                        <div className="flex items-center">
                                            <InputField
                                                name="is_featured"
                                                label="Featured Post"
                                                type="checkbox"
                                            />
                                        </div>
                                        <div className="flex items-center">
                                            <InputField
                                                name="is_priority"
                                                label="Priority Post"
                                                type="checkbox"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Featured Image */}
                                <div className="space-y-4">
                                    <h3 className="text-lg font-medium text-gray-900">Featured Image</h3>

                                    <div>
                                        <ImagePickerField
                                            label="Featured Image"
                                            preloadedImage={previewImage}
                                            onImageSelect={handleImageSelect}
                                            onClear={handleImageClear}
                                            placeholder="Upload featured image"
                                            acceptedFormats=".jpg,.png,.jpeg,.webp"
                                            maxFileSize={10}
                                        />
                                    </div>
                                </div>

                                {/* Social Media Links */}
                                <div className="space-y-4">
                                    <div className="flex items-center gap-2">
                                        <h3 className="text-lg font-medium text-gray-900">Social Media Links</h3>
                                        <FaLink className="text-gray-400" size={16} />
                                    </div>
                                    <p className="text-sm text-gray-600">Add social media URLs for this blog post (optional)</p>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div>
                                            <div className="flex items-center gap-2 mb-1">
                                                <FaInstagram className="text-pink-500" size={16} />
                                                <label className="text-sm font-medium text-gray-700">Instagram URL</label>
                                            </div>
                                            <InputField
                                                name="instagram"
                                                placeholder="https://instagram.com/your-post"
                                                type="url"
                                            />
                                        </div>
                                        <div>
                                            <div className="flex items-center gap-2 mb-1">
                                                <FaFacebook className="text-blue-600" size={16} />
                                                <label className="text-sm font-medium text-gray-700">Facebook URL</label>
                                            </div>
                                            <InputField
                                                name="facebook"
                                                placeholder="https://facebook.com/your-post"
                                                type="url"
                                            />
                                        </div>
                                        <div>
                                            <div className="flex items-center gap-2 mb-1">
                                                <FaTwitter className="text-blue-400" size={16} />
                                                <label className="text-sm font-medium text-gray-700">Twitter URL</label>
                                            </div>
                                            <InputField
                                                name="twitter"
                                                placeholder="https://twitter.com/your-post"
                                                type="url"
                                            />
                                        </div>
                                        <div>
                                            <div className="flex items-center gap-2 mb-1">
                                                <FaLinkedin className="text-blue-700" size={16} />
                                                <label className="text-sm font-medium text-gray-700">LinkedIn URL</label>
                                            </div>
                                            <InputField
                                                name="linkedin"
                                                placeholder="https://linkedin.com/your-post"
                                                type="url"
                                            />
                                        </div>
                                        <div>
                                            <div className="flex items-center gap-2 mb-1">
                                                <FaYoutube className="text-red-600" size={16} />
                                                <label className="text-sm font-medium text-gray-700">YouTube URL</label>
                                            </div>
                                            <InputField
                                                name="youtube"
                                                placeholder="https://youtube.com/your-post"
                                                type="url"
                                            />
                                        </div>
                                        <div>
                                            <div className="flex items-center gap-2 mb-1">
                                                <FaMedium className="text-gray-800" size={16} />
                                                <label className="text-sm font-medium text-gray-700">Medium URL</label>
                                            </div>
                                            <InputField
                                                name="medium"
                                                placeholder="https://medium.com/your-post"
                                                type="url"
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <div className="flex items-center gap-2 mb-1">
                                            <FaLink className="text-gray-500" size={16} />
                                            <label className="text-sm font-medium text-gray-700">Other Social Media URL</label>
                                        </div>
                                        <InputField
                                            name="other"
                                            placeholder="https://example.com/your-post"
                                            type="url"
                                        />
                                    </div>
                                </div>

                                <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                                    <Button
                                        type="button"
                                        onClick={handleCancel}
                                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        type="submit"
                                        disabled={isSubmitting || loading}
                                        className="px-4 py-2 text-sm font-medium text-white bg-primary border border-transparent rounded-md hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50"
                                    >
                                        {loading ? 'Saving...' : (isEdit ? 'Update Post' : 'Create Post')}
                                    </Button>
                                </div>
                            </Form>
                        )}
                    </Formik>
                </div>
            </div>

            {/* Quick Create Modals */}
            <ShowModal
                isOpen={showCategoryModal}
                onRequestClose={() => setShowCategoryModal(false)}
                title="Create New Category"
                modalWidth="50%"
            >
                <BlogCategoryFormPage
                    hideHeader={true}
                    onSuccess={handleCategorySuccess}
                    onCancel={() => setShowCategoryModal(false)}
                />
            </ShowModal>

            <ShowModal
                isOpen={showTagModal}
                onRequestClose={() => setShowTagModal(false)}
                title="Create New Tag"
                modalWidth="40%"
            >
                <BlogTagFormPage
                    hideHeader={true}
                    onSuccess={handleTagSuccess}
                    onCancel={() => setShowTagModal(false)}
                />
            </ShowModal>
        </>
    );
};

export default BlogPostFormPage;
